import 'react-datepicker/dist/react-datepicker.css';

import { CheckIcon, ChevronDownIcon } from '@chakra-ui/icons';
import {
  Box,
  Divider,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  useBoolean,
  useOutsideClick,
  useMediaQuery,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { BsArrowRight } from 'react-icons/bs';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import moment from 'moment';

import Calendar from 'components/Icons/Calendar';
import PlanPermission from 'container/PlanPermission';
import { TimeProperties } from 'models/response/model.interface';
import DatePickerInputButton from 'pages/ModelOverviewPage/DatePickerWithButton';
import TimeScalePermission from 'pages/PermissionComponent/TimeScalePermission';
import { getBlocks } from 'redux/ModelsSlice';
import { modelDetailStatus } from 'redux/PlanPageSlice';
import { getUserSubscriptionLimits } from 'redux/UserSlice';
import { getDashboard, setLoading } from 'redux/DashboardSlice';
import { AppDispatch } from 'utils/GlobalHelpers';
import {
  type ReportingWindow,
  REPORTING_WINDOW_LABELS,
  macroToReportingContext,
  resolveReportingPeriod,
  formatReportingDisplay,
  yyyymmToDate,
  dateToYyyyMm,
  getCurrentMonthYyyyMm,
} from 'utils/reportingPeriodUtils';
import {
  setTimeFilter,
  setReportingContext,
  getReportingAnchorPeriod,
  getReportingWindow,
  getIsPageBarOpen,
  getSelectedPage,
  getSelectedDashboardScenario,
  fetchAllDataValues,
  getTimeFilter,
} from 'redux/DashboardSliceV2';

export interface Properties {
  planPermission: boolean;
  rangeSelected: (start: string, end: string) => void;
}

const TimeRangeFilter = ({ planPermission, rangeSelected }: Properties) => {
  // Chakra typings can hit TS complexity limits in this large component.
  // Using `any` for these wrapper components avoids "union type too complex" errors.
  const PopoverContentAny = PopoverContent as any;
  const PopoverBodyAny = PopoverBody as any;
  const DividerAny = Divider as any;
  const MenuAny = Menu as any;
  const MenuButtonAny = MenuButton as any;
  const MenuListAny = MenuList as any;
  const MenuItemAny = MenuItem as any;
  const dispatch: AppDispatch = useDispatch();
  const { modelId } = useParams();
  const [timescale, setTimescale] = useState('Y');
  const [maxValue, setMaxValue] = useState(3);
  const [periodAmount, setPeriodAmount] = useState('3');
  const [endIn, setEndIn] = useState<Date | any>(new Date());
  const [isPermissionTimescaleModal, setIsPermissionTimescaleModal] = useState(false);
  const [startIn, setStartIn] = useState<Date | any>(new Date());
  const model = useSelector(getBlocks);
  const modelStatus = useSelector(modelDetailStatus);
  const UserSubscriptionLimitData = useSelector(getUserSubscriptionLimits);
  const reference = useRef() as React.MutableRefObject<HTMLInputElement>;
  const presetReference = useRef() as React.MutableRefObject<HTMLInputElement>;
  const [timeScalePopover, setTimeScalePopover] = useBoolean();
  const [presetPopover, setPresetPopover] = useBoolean();
  const [filterStartDate, setFilterStartDate] = useState<Date | any>();
  const [filterEndDate, setFilterEndDate] = useState<Date | any>();
  const reduxTimeFilter = useSelector(getTimeFilter);
  // Reporting period — new anchor + window model
  const reduxAnchor = useSelector(getReportingAnchorPeriod);
  const reduxWindow = useSelector(getReportingWindow);
  const [reportingPreset, setReportingPreset] = useState<string>('Current Month');
  /** Anchor month for Custom mode — stored as YYYY-MM. */
  const [customAnchor, setCustomAnchor] = useState<string>(getCurrentMonthYyyyMm());
  const [customAnchorDateObject, setCustomAnchorDateObject] = useState<Date | null>(
    yyyymmToDate(getCurrentMonthYyyyMm()),
  );
  const [customWindow, setCustomWindow] = useState<ReportingWindow>('DISPLAY_TIMESCALE');
  const formatTimeProperties = (properties: TimeProperties) => {
    return { ...properties, time_granularity: properties?.time_granularity ?? 'M' };
  };
  const properties = formatTimeProperties(model?.time_properties);
  const fyStartMonth = useMemo(() => {
    const month = model?.time_properties?.fy_start_month;
    return (month as string | undefined) ?? undefined;
  }, [model?.time_properties?.fy_start_month]);
  const timeGranualityLimit =
    UserSubscriptionLimitData?.subscription_features_limit?.model_timescale;
  const dashboard = useSelector(getDashboard);
  const [smallDevice] = useMediaQuery('(max-width: 1100px)');
  const isPageBarOpen = (useSelector(getIsPageBarOpen) && smallDevice) || smallDevice;
  const selectedPage = useSelector(getSelectedPage);
  const selectedDashboardScenario = useSelector(getSelectedDashboardScenario);
  const isSmallDevice = useMediaQuery('(max-width: 1024px)') && isPageBarOpen;
  const formatFilterDate = () => {
    if (filterStartDate && filterEndDate) {
      const startDate = new Date(filterStartDate).toDateString();
      setFilterStartDate(
        timescale === 'Y' ? dayjs(startDate).format('YYYY') : dayjs(startDate).format('DD MMM YY'),
      );
      const endDate = new Date(filterEndDate).toDateString();
      setFilterEndDate(
        timescale === 'Y' ? dayjs(endDate).format('YYYY') : dayjs(endDate).format('DD MMM YY'),
      );
    }
  };

  const onRangeSelected = (start: string, end: string) => {
    rangeSelected(start, end);
    setTimeScalePopover.off();
  };

  useEffect(() => {
    if (timeGranualityLimit === null) {
      setMaxValue(-1);
    } else {
      if (timescale.toLowerCase() === 'm') {
        setMaxValue(timeGranualityLimit);
      }
      if (timescale.toLowerCase() === 'y') {
        setMaxValue(timeGranualityLimit / 12);
      }
    }
  }, [timescale, timeGranualityLimit]);

  useEffect(() => {
    formatFilterDate();
  }, [filterStartDate, filterEndDate]);
  useEffect(() => {
    if (properties) {
      const timeGranularity = properties?.time_granularity ?? 'M';
      setTimescale(timeGranularity.toUpperCase());
      const value = Number.isNaN(properties?.plan_duration_periods)
        ? timeGranularity.toLowerCase() === 'm'
          ? 36
          : 3
        : Number(properties?.plan_duration_periods);
      setPeriodAmount(value.toString());
      setStartIn(properties?.starting_period ? new Date(properties?.starting_period) : null);
      setFilterStartDate(
        properties?.starting_period ? new Date(properties?.starting_period) : null,
      );
    }
    if (properties && properties?.starting_period) {
      const endDate = new Date(properties?.starting_period);
      const timeGranularity = properties?.time_granularity ?? 'M';

      const value = Number.isNaN(properties?.plan_duration_periods)
        ? timeGranularity.toLowerCase() === 'm'
          ? 36
          : 3
        : Number(properties?.plan_duration_periods);
      const targetValue = timeGranularity.toLowerCase() === 'm' ? value : value * 12;
      const dateEnd = endDate.setMonth(endDate.getMonth() + targetValue);
      setEndIn(new Date(dateEnd));
      setFilterEndDate(new Date(dateEnd));
    } else {
      setEndIn(null);
    }
  }, [modelStatus]);

  // Ensure timeFilter is always initialised in Redux from the model defaults.
  // Without this, users who haven't interacted with the timescale picker would
  // have a null timeFilter, causing the export (and any code that reads it) to
  // receive empty start/end dates.
  useEffect(() => {
    if (reduxTimeFilter || !properties?.starting_period) return;
    const timeGranularity = properties?.time_granularity ?? 'M';
    const value = Number.isNaN(properties?.plan_duration_periods)
      ? timeGranularity.toLowerCase() === 'm'
        ? 36
        : 3
      : Number(properties?.plan_duration_periods);
    const start = new Date(properties.starting_period);
    const end = new Date(properties.starting_period);
    end.setMonth(end.getMonth() + (timeGranularity.toLowerCase() === 'm' ? value : value * 12));
    const fmt = (date: Date) => {
      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      return timeGranularity.toLowerCase() === 'y'
        ? date.getFullYear().toString()
        : `${months[date.getMonth()]}-${date.getFullYear().toString().slice(-2)}`;
    };
    dispatch(setTimeFilter({ start: fmt(start), end: fmt(end) }));
  }, [properties?.starting_period, reduxTimeFilter]);

  const getExactDate = (dateString: string) => {
    if (properties?.time_granularity?.toLowerCase() === 'y') return dateString;
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    const dd = new Date(dateString);
    const month = months[dd.getMonth()]; // Get the month name from the array
    const year = dd.getFullYear().toString().slice(-2); // Get the last two digits of the year
    const newString = `${month}-${year}`;
    return newString;
  };

  const convertToDate = (input: string) => {
    const months: any = {
      Jan: 0,
      Feb: 1,
      Mar: 2,
      Apr: 3,
      May: 4,
      Jun: 5,
      Jul: 6,
      Aug: 7,
      Sep: 8,
      Oct: 9,
      Nov: 10,
      Dec: 11,
    };

    // Validate the input format
    const match = input.match(/^([A-Za-z]{3})-(\d{2})$/);
    if (!match) return null;

    const [, month, yearSuffix] = match;
    const monthIndex = months[month];
    if (monthIndex === undefined) return null;

    // Convert the year suffix to a full year
    const currentYear = new Date().getFullYear();
    const century = Math.floor(currentYear / 100) * 100;
    const year = century + Number.parseInt(yearSuffix, 10);

    // Create the date object and format it
    const date = new Date(year, monthIndex, 1);
    return date;
  };

  const isValidMonthYearFormat = (input: string): boolean => {
    // Regular expression to check "MMM-YY" format (e.g., "Jan-26", "Feb-24")
    const monthYearRegex = /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-\d{2}$/;

    // Check if input matches the pattern
    if (!monthYearRegex.test(input)) return false;

    // Extract year and convert to a full four-digit year
    const [, month, shortYear] =
      input.match(/(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-(\d{2})/) || [];
    if (!month || !shortYear) return false;

    // Convert short year to full year (assume 20XX for simplicity)
    const fullYear = Number.parseInt(`20${shortYear}`, 10);

    // Check if the parsed date is valid
    return moment(`${month} ${fullYear}`, 'MMM YYYY', true).isValid();
  };

  useEffect(() => {
    if (properties && dashboard?.id && modelId) {
      dispatch(setLoading(true));
      const planFilters = localStorage.getItem('global-modal-filters');
      const data = planFilters ? JSON.parse(planFilters) : {};
      const modalFilters = data[modelId] || {};
      const object = dashboard?.id && modalFilters.timeFilter ? modalFilters.timeFilter || {} : {};
      if (
        object &&
        dashboard?.id &&
        isValidMonthYearFormat(object?.startDatePlanPage) &&
        isValidMonthYearFormat(object?.endDatePlanPage)
      ) {
        const { startDatePlanPage, endDatePlanPage } = object;
        setStartIn(convertToDate(startDatePlanPage));
        setEndIn(convertToDate(endDatePlanPage));
        onRangeSelected(startDatePlanPage, endDatePlanPage);
        dispatch(setTimeFilter({ start: startDatePlanPage, end: endDatePlanPage }));
      } else {
        const timeGranularity = properties?.time_granularity ?? 'M';
        setTimescale(timeGranularity.toUpperCase());
        const value = Number.isNaN(properties?.plan_duration_periods)
          ? timeGranularity.toLowerCase() === 'm'
            ? 36
            : 3
          : Number(properties?.plan_duration_periods);
        setPeriodAmount(value.toString());
        setStartIn(properties?.starting_period ? new Date(properties?.starting_period) : null);
        setFilterStartDate(
          properties?.starting_period ? new Date(properties?.starting_period) : null,
        );

        const endDate = new Date(properties?.starting_period);

        const targetValue = timeGranularity.toLowerCase() === 'm' ? value : value * 12;
        const dateEnd = endDate.setMonth(endDate.getMonth() + targetValue);
        setEndIn(new Date(dateEnd));
        setFilterEndDate(new Date(dateEnd));
        onRangeSelected(
          getExactDate(dayjs(new Date(properties.starting_period)).format('DD MMM YY')),
          getExactDate(dayjs(new Date(dateEnd)).format('DD MMM YY')),
        );
        dispatch(
          setTimeFilter({
            start: getExactDate(new Date(properties.starting_period).toLocaleString()),
            end: getExactDate(new Date(dateEnd).toLocaleString()),
          }),
        );
      }
      dispatch(setLoading(false));
    }
  }, [dashboard?.id, modelId]);

  const applyPlanPageFilter = (startDatePlanPage: string, endDatePlanPage: string) => {
    onRangeSelected(startDatePlanPage, endDatePlanPage);
    dispatch(setTimeFilter({ start: startDatePlanPage, end: endDatePlanPage }));

    const planFilters = localStorage.getItem('global-modal-filters');
    const data = planFilters ? JSON.parse(planFilters) : {};
    if (modelId) {
      const modalFilters = data[modelId] || {};

      // Ensure timeFilter exists
      if (!modalFilters.timeFilter) {
        modalFilters.timeFilter = {};
      }

      // Set the time range for the dashboard id
      modalFilters.timeFilter = { startDatePlanPage, endDatePlanPage };

      // Save back to localStorage
      localStorage.setItem(
        'global-modal-filters',
        JSON.stringify({ ...data, [modelId]: modalFilters }),
      );
    }
  };

  const changeTimeInput = useCallback(
    (time: string, amount: string, startDate: Date | null, endDate?: Date | null) => {
      const requestObject: any = {
        model_id: Number(modelId),
        request_body: {
          time_granularity: time,
          plan_duration_periods: amount,
        },
      };
      if (endDate) {
        const endInDate = endDate.toDateString();
        requestObject.request_body.ending_period =
          time === 'Y' ? dayjs(endInDate).format('YYYY') : dayjs(endInDate).format('DD MMM YY');
      }
      if (startDate !== null) {
        const startInDate = startDate.toDateString();
        requestObject.request_body.starting_period =
          time === 'Y' ? dayjs(startInDate).format('YYYY') : dayjs(startInDate).format('DD MMM YY');
      }
      const endDatePlanPage = getExactDate(requestObject.request_body.ending_period);
      const startDatePlanPage = getExactDate(requestObject.request_body.starting_period);
      applyPlanPageFilter(startDatePlanPage, endDatePlanPage);
    },
    [modelId, filterStartDate, filterEndDate],
  );

  const onChangePeriod = async (valueDiff: any) => {
    const newValue = valueDiff;
    if (Number(newValue) <= maxValue) {
      setPeriodAmount(newValue);
    } else if (maxValue === -1) {
      setPeriodAmount(newValue);
    } else {
      setIsPermissionTimescaleModal(true);
      setTimeout(() => {
        const timeGranularity = properties?.time_granularity ?? 'M';
        const value = Number.isNaN(properties?.plan_duration_periods)
          ? timeGranularity.toLowerCase() === 'm'
            ? 36
            : 3
          : Number(properties?.plan_duration_periods);
        setPeriodAmount(value.toString());
      }, 1000);
      setTimeout(() => {
        setIsPermissionTimescaleModal(false);
      }, 5000);
    }
  };

  const startDateChange = (startDate: Date) => {
    if (timescale.toLowerCase() === 'm') {
      const monthsApart =
        (endIn.getFullYear() - startDate.getFullYear()) * 12 +
        (endIn.getMonth() - startDate.getMonth());
      setPeriodAmount(`${monthsApart}`);
      onChangePeriod(monthsApart);
      return `${monthsApart}`;
    }
    const yearDifference = endIn.getFullYear() - startDate.getFullYear();
    setPeriodAmount(yearDifference.toString());
    onChangePeriod(`${yearDifference}`);
    return `${yearDifference}`;
  };

  const endDateChange = (endDate: Date) => {
    if (timescale.toLowerCase() === 'm') {
      const monthsApart =
        (endDate.getFullYear() - startIn.getFullYear()) * 12 +
        (endDate.getMonth() - startIn.getMonth());
      setPeriodAmount(`${monthsApart}`);
      onChangePeriod(monthsApart);
      return `${monthsApart}`;
    }
    const yearDifference = endDate.getFullYear() - startIn.getFullYear();
    setPeriodAmount(yearDifference.toString());
    onChangePeriod(`${yearDifference}`);
    return `${yearDifference}`;
  };

  const onSelectStartDate = async (date: Date) => {
    setStartIn(date);
    const diff = startDateChange(date);
    onChangePeriod(Number(diff));
    changeTimeInput(timescale, diff, date, endIn);
  };

  const onSelectEndDate = useCallback(
    (date: Date) => {
      setEndIn(date);
      const diff = endDateChange(date);
      onChangePeriod(Number(diff));
      changeTimeInput(timescale, diff, startIn, date);
    },
    [changeTimeInput, periodAmount, timescale],
  );

  const handleClearSelection = () => {
    setStartIn(null);
  };

  /**
   * Persists the reporting context to localStorage and dispatches to Redux.
   * For non-Custom presets the anchor is computed live from the preset so we
   * don't cache a stale date.
   */
  const applyReportingContext = useCallback(
    (preset: string, anchor: string, window: ReportingWindow) => {
      dispatch(
        setReportingContext({
          reporting_anchor_period: anchor,
          reporting_window: window,
          preset,
        }),
      );

      if (model?.id) {
        const planFilters = localStorage.getItem('global-modal-filters');
        const existing = planFilters ? JSON.parse(planFilters) : {};
        const modalFilters = existing[model.id] || {};
        // Store new shape: { preset, anchor, window }
        modalFilters.reportingPeriod = { preset, anchor, window };
        localStorage.setItem(
          'global-modal-filters',
          JSON.stringify({ ...existing, [model.id]: modalFilters }),
        );
      }

      // Trigger data fetch
      if (selectedPage?.id && selectedDashboardScenario?.id) {
        dispatch(
          fetchAllDataValues({
            pageId: selectedPage.id,
            scenarioId: selectedDashboardScenario.id,
          }),
        );
      }
    },
    [dispatch, model?.id, selectedPage?.id, selectedDashboardScenario?.id],
  );

  /** Handles selection of a macro preset or opening Custom mode. */
  const handlePresetChange = (preset: string) => {
    setReportingPreset(preset);
    setPresetPopover.off();

    if (preset === 'Custom') {
      // Keep the outer popover open so the user can pick an anchor + window
      return;
    }

    // Map macro preset to its anchor + window
    const reportingContext = macroToReportingContext(preset);
    applyReportingContext(
      preset,
      reportingContext.reporting_anchor_period,
      reportingContext.reporting_window,
    );
  };

  /** Called when the user changes the anchor month in Custom mode. */
  const handleCustomAnchorChange = (date: Date | null) => {
    if (!date) return;
    const anchor = dateToYyyyMm(date);
    setCustomAnchorDateObject(date);
    setCustomAnchor(anchor);
    applyReportingContext('Custom', anchor, customWindow);
  };

  /** Called when the user changes the reporting window in Custom mode. */
  const handleCustomWindowChange = (window: ReportingWindow) => {
    setCustomWindow(window);
    applyReportingContext('Custom', customAnchor, window);
  };

  // ── Load saved reporting period from localStorage on mount ──────────────────
  useEffect(() => {
    if (!model?.id) return;

    const planFilters = localStorage.getItem('global-modal-filters');
    const existing = planFilters ? JSON.parse(planFilters) : {};
    const modalFilters = existing[model.id] || {};
    const saved = modalFilters.reportingPeriod;

    if (!saved) return;

    // ── Migration: old shape had { preset, start, end }; new shape has { preset, anchor, window }.
    // If the cached data has `start`/`end` but no `anchor`, this is old Custom data we can't
    // reverse-engineer cleanly.  Reset to Current Month the first time so the user re-selects.
    if (saved.preset === 'Custom' && saved.start && !saved.anchor) {
      const defaultContext = macroToReportingContext('Current Month');
      setReportingPreset('Current Month');
      applyReportingContext(
        'Current Month',
        defaultContext.reporting_anchor_period,
        defaultContext.reporting_window,
      );
      // Overwrite stale cache entry
      modalFilters.reportingPeriod = {
        preset: 'Current Month',
        anchor: defaultContext.reporting_anchor_period,
        window: defaultContext.reporting_window,
      };
      localStorage.setItem(
        'global-modal-filters',
        JSON.stringify({ ...existing, [model.id]: modalFilters }),
      );
      return;
    }

    // New shape — restore as-is
    const restoredPreset: string = saved.preset || 'Current Month';
    const restoredAnchor: string = saved.anchor || getCurrentMonthYyyyMm();
    const restoredWindow: ReportingWindow = saved.window || 'SINGLE';

    setReportingPreset(restoredPreset);
    if (restoredPreset === 'Custom') {
      setCustomAnchor(restoredAnchor);
      setCustomAnchorDateObject(yyyymmToDate(restoredAnchor));
      setCustomWindow(restoredWindow);
    }
    dispatch(
      setReportingContext({
        reporting_anchor_period: restoredAnchor,
        reporting_window: restoredWindow,
        preset: restoredPreset,
      }),
    );
  }, [model?.id]);

  // ── Keep local state in sync when Redux is changed externally ───────────────
  useEffect(() => {
    if (reduxAnchor && reduxWindow) {
      setCustomAnchor(reduxAnchor);
      setCustomAnchorDateObject(yyyymmToDate(reduxAnchor));
      setCustomWindow(reduxWindow);
    }
  }, [reduxAnchor, reduxWindow]);

  useOutsideClick({
    ref: reference,
    handler: () => {
      setTimeScalePopover.off();
    },
  });

  useOutsideClick({
    ref: presetReference,
    handler: () => {
      setPresetPopover.off();
    },
  });
  return (
    <>
      <Box
        display='flex'
        ps={0}
        gap={1}
        mr={2}
        borderLeftColor='white'
        backgroundColor='#332663'
        padding='.5rem 1rem'
        borderRightRadius='sm'
        height='40px'
        width='max-content'
      >
        <Popover
          placement='bottom-start'
          closeOnBlur={false}
          isOpen={timeScalePopover}
          onOpen={setTimeScalePopover.on}
          onClose={setTimeScalePopover.off}
          isLazy
          lazyBehavior='keepMounted'
        >
          <Box ref={reference}>
            <PopoverTrigger>
              <Box
                display='flex'
                gap={1}
                cursor='pointer'
                alignItems='center'
                title={
                  reduxAnchor ? formatReportingDisplay(reduxAnchor, reduxWindow) : reportingPreset
                }
              >
                <Calendar
                  style={{
                    marginRight: '.2rem',
                  }}
                  width={5}
                  height={6}
                  color='#69B4B8'
                />
                <ChevronDownIcon w={5} h={5} />
                {!isSmallDevice && (
                  <Box as='span' ml={1} fontSize='14px'>
                    {reduxAnchor
                      ? formatReportingDisplay(reduxAnchor, reduxWindow)
                      : reportingPreset}
                  </Box>
                )}
              </Box>
            </PopoverTrigger>
            <PopoverContentAny
              filter='drop-shadow(0px 0px 8px rgba(0, 0, 0, 0.25))'
              bg='white'
              border='none'
              mt={3}
              borderRadius='8px'
              minW='260px'
            >
              <PopoverBodyAny px={4} py={3}>
                <Box pt={4} pb={2}>
                  <Box as='span' color='#6562CF' fontSize='xs' fontWeight='600' display='block'>
                    Preset
                  </Box>
                </Box>
                <Popover
                  placement='bottom-start'
                  closeOnBlur={false}
                  isOpen={presetPopover}
                  onOpen={setPresetPopover.on}
                  onClose={setPresetPopover.off}
                  isLazy
                  lazyBehavior='keepMounted'
                >
                  <Box ref={presetReference}>
                    <PopoverTrigger>
                      <Box
                        display='flex'
                        alignItems='center'
                        justifyContent='space-between'
                        cursor='pointer'
                        px={3}
                        h='32px'
                        borderRadius='8px'
                        border='1px solid #CBD5E0'
                        bg='white'
                        _hover={{ bg: 'gray.50' }}
                        mb={3}
                      >
                        <Box as='span' color='gray.700' fontSize='xs'>
                          {reportingPreset}
                        </Box>
                        <ChevronDownIcon boxSize={4} color='gray.500' />
                      </Box>
                    </PopoverTrigger>
                    <PopoverContentAny
                      ml={{ base: '5', md: '3' }}
                      filter='drop-shadow(0px 0px 8px rgba(0, 0, 0, 0.25))'
                      bg='white'
                      maxWidth='80vw'
                      borderRadius='14px'
                    >
                      <Box pt={4} pb={1} px={5}>
                        <Box
                          as='span'
                          color='#6562CF'
                          fontSize='xs'
                          fontWeight='600'
                          display='block'
                        >
                          Select Preset
                        </Box>
                      </Box>
                      <PopoverBodyAny>
                        <Box display='flex' flexDirection='column'>
                          {[
                            'Current Month',
                            'Last Month',
                            'Financial Year to Last Month',
                            'This Financial Year',
                            'Custom',
                          ].map((preset) => {
                            // Compute the preview label for each preset option
                            const previewContext =
                              preset !== 'Custom' ? macroToReportingContext(preset) : null;
                            const previewLabel = previewContext
                              ? formatReportingDisplay(
                                  previewContext.reporting_anchor_period,
                                  previewContext.reporting_window,
                                )
                              : 'Pick anchor & window below';

                            return (
                              <Box
                                display='flex'
                                key={preset}
                                alignItems={'center'}
                                justifyContent={'space-between'}
                                cursor='pointer'
                                borderRadius='14px'
                                px='1'
                                py='2'
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handlePresetChange(preset);
                                }}
                                bg={reportingPreset === preset ? '#EFEEFE' : 'transparent'}
                              >
                                <Box
                                  display={'flex'}
                                  color={'#8B89B1'}
                                  alignItems={'center'}
                                  w={'100%'}
                                >
                                  {reportingPreset === preset && <CheckIcon />}
                                  <Box flex={1} px='2' py='0'>
                                    <Box as='span' color='black' fontSize='xs' display='block'>
                                      {preset}
                                    </Box>
                                    <Box
                                      as='span'
                                      color='#8B8AAB'
                                      fontSize='10px'
                                      mt={0.5}
                                      display='block'
                                    >
                                      {previewLabel}
                                    </Box>
                                  </Box>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </PopoverBodyAny>
                    </PopoverContentAny>
                  </Box>
                </Popover>
                {reportingPreset === 'Custom' && (
                  <>
                    <Box pb={2}>
                      <Box as='span' color='#6562CF' fontSize='xs' fontWeight='600' display='block'>
                        Anchor Month
                      </Box>
                    </Box>
                    <Box pb={3}>
                      {/* Single month picker — the anchor month for the reporting window */}
                      <DatePickerInputButton
                        clearSection={false}
                        clearDateInput={() => {}}
                        selected={customAnchorDateObject}
                        onChange={handleCustomAnchorChange}
                        timescale='M'
                        isDisabled={planPermission}
                        minDate={filterStartDate ? new Date(filterStartDate) : undefined}
                        maxDate={filterEndDate ? new Date(filterEndDate) : undefined}
                        placeholder='Anchor month'
                        styles={{ height: '32px', width: '100%' }}
                      />
                    </Box>
                    <Box pb={2}>
                      <Box as='span' color='#6562CF' fontSize='xs' fontWeight='600' display='block'>
                        Time Window
                      </Box>
                    </Box>
                    <MenuAny matchWidth>
                      <MenuButtonAny
                        w='100%'
                        h='32px'
                        mb={3}
                        px={3}
                        bg='white'
                        borderRadius='8px'
                        border='1px solid #CBD5E0'
                        _hover={{ bg: 'gray.50' }}
                        _active={{ bg: 'gray.50' }}
                        _focus={{ borderColor: '#6562CF', boxShadow: '0 0 0 1px #6562CF' }}
                      >
                        <Box display='flex' alignItems='center' justifyContent='space-between'>
                          <Box as='span' fontSize='xs' color='gray.700' fontWeight='normal'>
                            {REPORTING_WINDOW_LABELS[customWindow]}
                          </Box>
                          <ChevronDownIcon boxSize={4} color='gray.500' flexShrink={0} />
                        </Box>
                      </MenuButtonAny>
                      <MenuListAny zIndex={20} minW={0} borderRadius='8px' py={1} shadow='md'>
                        {(Object.keys(REPORTING_WINDOW_LABELS) as ReportingWindow[]).map((key) => (
                          <MenuItemAny
                            key={key}
                            onClick={() => handleCustomWindowChange(key)}
                            bg={customWindow === key ? '#EFEEFE' : 'white'}
                            color='gray.700'
                            fontSize='xs'
                            _hover={{ bg: 'gray.100' }}
                          >
                            {REPORTING_WINDOW_LABELS[key]}
                          </MenuItemAny>
                        ))}
                      </MenuListAny>
                    </MenuAny>
                    {customAnchor && (
                      <Box as='span' color='#8B8AAB' fontSize='12px' ps={1} pb={2} display='block'>
                        {(() => {
                          const { start, end } = resolveReportingPeriod(
                            customAnchor,
                            customWindow,
                            fyStartMonth,
                          );
                          return start === end ? start : `${start} – ${end}`;
                        })()}
                      </Box>
                    )}
                  </>
                )}
                <DividerAny orientation='horizontal' mb={3} />
                <Box pb={2}>
                  <Box as='span' color='#6562CF' fontSize='xs' fontWeight='600' display='block'>
                    Display Timescale
                  </Box>
                </Box>
                <Box display='flex' alignItems='center' pb={3} gap={2}>
                  <Box flex='1'>
                    <DatePickerInputButton
                      clearSection={false}
                      clearDateInput={handleClearSelection}
                      selected={startIn}
                      onChange={onSelectStartDate}
                      timescale={timescale}
                      isDisabled={planPermission}
                      minDate={new Date(filterStartDate)}
                      maxDate={new Date(endIn)}
                      styles={{ height: '32px', width: '100%' }}
                    />
                  </Box>
                  <BsArrowRight
                    color='black'
                    style={{ width: '14px', height: '14px', flexShrink: 0 }}
                  />
                  <Box flex='1'>
                    <DatePickerInputButton
                      clearSection={false}
                      clearDateInput={handleClearSelection}
                      selected={endIn}
                      onChange={onSelectEndDate}
                      timescale={timescale}
                      isDisabled={planPermission}
                      minDate={new Date(startIn)}
                      maxDate={new Date(filterEndDate)}
                      styles={{ height: '32px', width: '100%' }}
                    />
                  </Box>
                </Box>
                <Box as='span' color='#8B8AAB' fontSize='12px' ps={1} display='block'>
                  {timescale === 'M'
                    ? `${Number(periodAmount) + 1} months`
                    : `${Number(periodAmount) + 1} years`}
                </Box>
                {isPermissionTimescaleModal && (
                  <TimeScalePermission maxValue={maxValue} timescale={timescale} />
                )}
              </PopoverBodyAny>
            </PopoverContentAny>
          </Box>
        </Popover>
      </Box>
    </>
  );
};

export default PlanPermission(TimeRangeFilter);
